import { Link } from 'react-router-dom';
import { ArrowLeft, Download, FileText } from 'lucide-react';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatLegalHeaderLine, LEGAL_VERSION } from '@/lib/marketing/legal';
import { PageSeo } from '@/components/seo/PageSeo';
import { NOINDEX_FOLLOW } from '@/lib/seo/constants';
import { excerpt } from '@/lib/seo/excerpt';

/** Served from `public/legal/` — keep in sync with RecruiterAI-BE/docs/legal/. */
export const DPA_DOWNLOAD_PATH = '/legal/HiroMatch-DPA-art28.docx';
export const DPA_FILENAME = 'HiroMatch-DPA-art28.docx';

const COPY = {
  it: {
    metaTitle: 'Accordo sul Trattamento dei Dati (DPA) | HiroMatch',
    title: 'Accordo sul Trattamento dei Dati (DPA)',
    intro:
      'Documento contrattuale ex Art. 28 GDPR tra G-One Srl (responsabile del trattamento) e il Cliente (titolare rispetto ai dati dei candidati trattati tramite HiroMatch). Fa parte integrante dei Termini di Servizio per i Clienti B2B.',
    download: 'Scarica il DPA (Word)',
    downloadHint: 'Formato .docx — adatto a revisione legale e firma.',
    relatedTitle: 'Documenti correlati',
    privacy: 'Informativa Privacy',
    terms: 'Termini di Servizio',
    contact: 'Per copie firmate, domande o adeguamenti specifici al tuo ordine contrattuale, usa il modulo di contatto.',
    contactCta: 'Modulo di contatto',
  },
  en: {
    metaTitle: 'Data Processing Agreement (DPA) | HiroMatch',
    title: 'Data Processing Agreement (DPA)',
    intro:
      'Art. 28 GDPR agreement between G-One Srl (processor) and the Customer (controller for candidate data processed through HiroMatch). It forms an integral part of the Terms of Service for B2B Customers.',
    download: 'Download the DPA (Word)',
    downloadHint: '.docx format — suitable for legal review and signature.',
    relatedTitle: 'Related documents',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    contact: 'For executed copies, questions, or contract-specific amendments, use the contact form.',
    contactCta: 'Contact form',
  },
} as const;

export default function DpaPage() {
  const { language } = useLanguage();
  const lang = language === 'it' ? 'it' : 'en';
  const copy = COPY[lang];

  return (
    <div className="min-h-screen bg-brand-surface-light text-foreground flex flex-col">
      <PageSeo
        title={copy.metaTitle}
        description={excerpt(copy.intro)}
        path="/dpa"
        robots={NOINDEX_FOLLOW}
      />
      <MarketingNav />
      <main className="flex-1 container py-10 md:py-14 max-w-3xl">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" asChild>
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            HiroMatch
          </Link>
        </Button>

        <header className="space-y-3 mb-10 pb-8 border-b border-border/60">
          <div className="flex items-center gap-3 text-brand-accent">
            <FileText className="h-8 w-8" aria-hidden />
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{copy.title}</h1>
          </div>
          <p className="text-sm text-muted-foreground">{formatLegalHeaderLine(lang)}</p>
          <p className="text-muted-foreground leading-relaxed">{copy.intro}</p>
        </header>

        <div className="rounded-lg border border-border/60 bg-card p-6 space-y-4">
          <Button asChild size="lg" className="w-full sm:w-auto gap-2">
            <a href={DPA_DOWNLOAD_PATH} download={DPA_FILENAME}>
              <Download className="h-4 w-4" />
              {copy.download}
            </a>
          </Button>
          <p className="text-sm text-muted-foreground">{copy.downloadHint}</p>
        </div>

        <section className="mt-10 space-y-3">
          <h2 className="font-display font-semibold text-lg">{copy.relatedTitle}</h2>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" className="text-brand-accent hover:underline">
              {copy.privacy}
            </Link>
            <Link to="/terms" className="text-brand-accent hover:underline">
              {copy.terms}
            </Link>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {copy.contact}{' '}
            <Link to="/contact" className="text-brand-accent hover:underline">
              {copy.contactCta}
            </Link>
            .
          </p>
        </section>

        <p className="mt-10 text-xs text-muted-foreground">
          {lang === 'it' ? `Versione documento: ${LEGAL_VERSION}` : `Document version: ${LEGAL_VERSION}`}
        </p>
      </main>
      <MarketingFooter />
    </div>
  );
}
